import { useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Overline } from "../components/Overline";
import { RESOURCES } from "../lib/site";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";

export default function Resources() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const requestDownload = (resource) => {
    setActive(resource);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !active) return;
    setLoading(true);
    try {
      await api.post("/resources/download", {
        email,
        name: name || null,
        resource_slug: active.slug,
      });
      toast.success(
        `"${active.title}" is on its way to your inbox.`
      );
      setOpen(false);
      setName("");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-resources" className="bg-[#faf9f6]">
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
        <div className="max-w-3xl">
          <Overline>Free Resources</Overline>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.04] font-normal text-[#333] mt-5">
            Begin gently  - 
            <br />
            <span className="italic">at home, on your own time.</span>
          </h1>
          <p className="mt-8 text-lg text-[#3d3d3d] font-normal leading-relaxed">
            Beautifully designed, evidence-grounded guides drawn from my
            practice. Pop in your email and I'll send them straight to you.
            You'll also become part of The Letter - slow, monthly notes on
            hormones and rhythm.
          </p>
        </div>

        <div className="mt-20 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {RESOURCES.map((r) => (
            <article
              key={r.slug}
              data-testid={`resource-${r.slug}`}
              className="bg-white border border-[#e5e0d8] rounded-3xl overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#5fa292]/5 flex flex-col"
            >
              <div className="aspect-[4/3] bg-[#cbdad6] overflow-hidden">
                <img
                  src={r.image}
                  alt={r.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-7 flex-1 flex flex-col">
                <p className="text-xs uppercase tracking-[0.2em] text-[#5fa292]">
                  {r.pages}
                </p>
                <h2 className="font-serif text-2xl text-[#333] mt-3 font-normal tracking-tight leading-snug">
                  {r.title}
                </h2>
                <p className="mt-3 text-[#3d3d3d] font-normal leading-relaxed text-sm flex-1">
                  {r.description}
                </p>
                <Button
                  data-testid={`resource-download-${r.slug}`}
                  onClick={() => requestDownload(r)}
                  className="mt-6 bg-[#5fa292] hover:bg-[#4d8275] text-white rounded-full h-11 w-full"
                >
                  <Download size={16} className="mr-2" />
                  Get it free
                </Button>
              </div>
            </article>
          ))}
        </div>
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="resource-dialog"
          className="bg-[#faf9f6] border-[#e5e0d8] max-w-md"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl font-normal text-[#333] tracking-tight">
              {active?.title}
            </DialogTitle>
            <DialogDescription className="text-[#3d3d3d] font-normal">
              Where shall I send it?
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={submit}
            data-testid="resource-form"
            className="space-y-4 mt-2"
          >
            <Input
              placeholder="First name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="resource-name-input"
              className="bg-white"
            />
            <Input
              type="email"
              required
              placeholder="Your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="resource-email-input"
              className="bg-white"
            />
            <Button
              type="submit"
              disabled={loading}
              data-testid="resource-submit"
              className="w-full bg-[#5fa292] hover:bg-[#4d8275] text-white rounded-full h-11"
            >
              {loading ? "Sending..." : "Send me the guide"}
            </Button>
            <p className="text-xs text-[#3d3d3d] text-center font-normal">
              I'll never share your email. Unsubscribe in one click.
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
